// @ts-nocheck
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { UseMutationOptions } from '@tanstack/react-query'
import { useAuth } from '@/contexts/auth-context'
import { api, ApiError } from '@/lib/api'
import { getApiBaseUrl, resolveAssetUrl } from '@/lib/utils'

// Helper hook to check if user is authenticated
const useIsAuthenticated = (): boolean => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return false; // Consider loading as unauthenticated for now
  }

  // User is authenticated if they have auth user
  return !!user;
}

// Analytics Hooks
export const useDashboardAnalytics = (params?: any) => {
  const isAuthenticated = useIsAuthenticated();
  const { user } = useAuth();

  return useQuery({
    queryKey: ['analyticsDashboard', user?.currentOrganizationId, params],
    queryFn: async () => {
      return await api.get('/dashboard/overview');
    },
    refetchInterval: isAuthenticated ? 1000 * 60 * 5 : false, // Refetch every 5 minutes only if authenticated
    staleTime: 1000 * 60 * 2, // Consider stale after 2 minutes
    enabled: isAuthenticated && !!user?.id && !!user?.currentOrganizationId, // Only fetch when org is selected
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
  })
}

// Feedback Hooks
export const useFeedbackCycles = () => {
  const isAuthenticated = useIsAuthenticated();
  const { user } = useAuth();

  return useQuery({
    queryKey: ['feedbackCycles', user?.id],
    queryFn: async () => {
      const response = await api.get('/feedback-cycles', {
        params: {
          status: 'active',
          page: 1,
          limit: 50,
        },
      })

      if (response && typeof response === 'object' && 'data' in response) {
        return response
      }

      return {
        success: true,
        data: Array.isArray(response) ? response : [],
      }
    },
    enabled: isAuthenticated && !!user?.id,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  })
}

export const useUserFeedbackResponses = (params?: { perspective?: 'received' | 'given'; limit?: number }) => {
  const isAuthenticated = useIsAuthenticated();
  const { user } = useAuth();

  return useQuery({
    queryKey: [
      'userFeedbackResponses',
      user?.id,
      params?.perspective ?? 'received',
      params?.limit ?? 50
    ],
    queryFn: async () => {
      const response = await api.get('/feedback/responses', {
        params: {
          perspective: params?.perspective ?? 'received',
          limit: params?.limit ?? 50
        }
      })

      if (response && typeof response === 'object' && 'data' in response) {
        return response
      }

      return {
        success: true,
        data: Array.isArray(response) ? response : [],
      }
    },
    enabled: isAuthenticated && !!user?.id,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  })
}

export const useFeedbackAnalytics = () => {
  const isAuthenticated = useIsAuthenticated();
  const { user } = useAuth();

  return useQuery({
    queryKey: ['feedbackAnalytics', user?.id],
    queryFn: async () => {
      const [receivedResponse, givenResponse, pendingResponse] = await Promise.all([
        api.get('/feedback/responses', {
          params: {
            perspective: 'received',
            limit: 100,
          },
        }),
        api.get('/feedback/responses', {
          params: {
            perspective: 'given',
            limit: 100,
          },
        }),
        api.get('/feedback/pending'),
      ])

      const extractDataArray = (payload: unknown) => {
        if (payload && typeof payload === 'object') {
          if ('data' in payload && Array.isArray(payload.data)) {
            return payload.data
          }
        }

        return Array.isArray(payload) ? payload : []
      }

      const received = extractDataArray(receivedResponse)
      const given = extractDataArray(givenResponse)
      const pendingReviews = extractDataArray(pendingResponse).length

      const numericRating = (value: unknown) => {
        if (value === null || value === undefined) {
          return null
        }

        const parsed = Number(value)
        return Number.isFinite(parsed) ? parsed : null
      }

      const ratingResponses = received
        .map((response: any) => ({
          rating: numericRating(response.responseRating),
          questionText: response?.question?.text ?? null,
          respondentId: response?.respondent?.id ?? null,
        }))
        .filter((entry) => entry.rating !== null)

      const ratingsCount = ratingResponses.length
      const totalRating = ratingResponses.reduce((sum, entry) => sum + (entry.rating ?? 0), 0)
      const overallRating = ratingsCount > 0 ? Number((totalRating / ratingsCount).toFixed(2)) : 0

      const contributors = new Set(
        received
          .map((response: any) => response?.respondent?.id)
          .filter((id: unknown): id is string => typeof id === 'string' && id.length > 0),
      ).size

      const questionStats = new Map<string, { total: number; count: number }>()
      ratingResponses.forEach((entry) => {
        const key = entry.questionText || 'Overall feedback'
        const current = questionStats.get(key) || { total: 0, count: 0 }
        current.total += entry.rating ?? 0
        current.count += 1
        questionStats.set(key, current)
      })

      const insights = Array.from(questionStats.entries()).map(([name, stats]) => {
        const average = stats.count > 0 ? stats.total / stats.count : 0
        return {
          name,
          rating: Number(average.toFixed(2)),
          score: Math.round(Math.min(Math.max(average * 20, 0), 100)),
        }
      })

      const strengths = [...insights].sort((a, b) => b.rating - a.rating).slice(0, 4)

      const strengthsNames = new Set(strengths.map((item) => item.name))
      const developmentCandidates = [...insights]
        .sort((a, b) => a.rating - b.rating)
        .filter((item) => !strengthsNames.has(item.name))
      const developmentAreas = developmentCandidates.slice(0, 4)
      const resolvedDevelopmentAreas =
        developmentAreas.length > 0
          ? developmentAreas
          : [...insights].sort((a, b) => a.rating - b.rating).slice(0, 4)

      const placeholder = [{ name: 'No rated feedback yet', score: 0, rating: 0 }]

      return {
        success: true,
        data: {
          metrics: {
            overallRating,
            totalReviews: received.length,
            ratingsCount,
            totalFeedbackReceived: received.length,
            totalFeedbackGiven: given.length,
            contributors,
            pendingReviews,
          },
          strengths: strengths.length > 0 ? strengths : placeholder,
          developmentAreas:
            resolvedDevelopmentAreas.length > 0 ? resolvedDevelopmentAreas : placeholder,
          receivedResponses: received,
          givenResponses: given,
        },
      }
    },
    enabled: isAuthenticated && !!user?.id,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  })
}

// Feedback CRUD operations
export const useCreateFeedback = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (feedbackData: {
      cycleId: string;
      targetUserId: string;
      responses: Array<{ questionId: string; text: string; rating?: number }>;
      isAnonymous?: boolean;
    }) => {
      return await api.post('/feedback', feedbackData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedbackCycles'] });
      queryClient.invalidateQueries({ queryKey: ['userFeedbackResponses'] });
      queryClient.invalidateQueries({ queryKey: ['feedbackAnalytics'] });
    },
  });
};

export const useUpdateFeedback = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: {
      id: string;
      responses?: Array<{ questionId: string; text: string; rating?: number }>;
      isAnonymous?: boolean;
    }) => {
      return await api.patch(`/feedback/${id}`, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedbackCycles'] });
      queryClient.invalidateQueries({ queryKey: ['userFeedbackResponses'] });
      queryClient.invalidateQueries({ queryKey: ['feedbackAnalytics'] });
    },
  });
};

export const useDeleteFeedback = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (feedbackId: string) => {
      return await api.delete(`/feedback/${feedbackId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedbackCycles'] });
      queryClient.invalidateQueries({ queryKey: ['userFeedbackResponses'] });
      queryClient.invalidateQueries({ queryKey: ['feedbackAnalytics'] });
    },
  });
};

export const useCreateFeedbackCycle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (cycleData: {
      name: string;
      description: string;
      targetUserId: string;
      participants: string[];
      questions: Array<{ text: string; type: 'text' | 'rating' | 'scale' }>;
      isRecurring?: boolean;
      scheduleConfig?: any;
    }) => {
      return await api.post('/feedback-cycles', cycleData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedbackCycles'] });
    },
  });
};

export const useUpdateFeedbackCycle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: {
      id: string;
      name?: string;
      description?: string;
      participants?: string[];
      questions?: Array<{ text: string; type: 'text' | 'rating' | 'scale' }>;
      isRecurring?: boolean;
      scheduleConfig?: any;
    }) => {
      return await api.patch(`/feedback-cycles/${id}`, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedbackCycles'] });
    },
  });
};

export const useDeleteFeedbackCycle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (cycleId: string) => {
      return await api.delete(`/feedback-cycles/${cycleId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedbackCycles'] });
    },
  });
};

export const usePendingFeedback = () => {
  const isAuthenticated = useIsAuthenticated();
  const { user } = useAuth();

  return useQuery({
    queryKey: ['pendingFeedback'],
    queryFn: async () => {
      return {
        success: true,
        data: [
          {
            id: '1',
            cycle: {
              id: 'cycle-1',
              name: 'Q3 Performance Review',
              description: 'Quarterly performance evaluation',
              type: 'Performance Review'
            },
            target: {
              id: 'target-1',
              name: 'Sarah Johnson',
              role: 'Senior Developer',
              avatar: null,
              initials: 'SJ',
              avatarColor: 'bg-purple-100 text-purple-700'
            },
            priority: 'high',
            isOverdue: true,
            daysPastDue: 3,
            dueDate: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(), // 3 days ago
            invitedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 10).toISOString(), // 10 days ago
            createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 10).toISOString()
          },
          {
            id: '2',
            cycle: {
              id: 'cycle-2',
              name: 'Team Collaboration Review',
              description: 'Monthly team feedback cycle',
              type: '360 Review'
            },
            target: {
              id: 'target-2',
              name: 'David Smith',
              role: 'Product Manager',
              avatar: null,
              initials: 'DS',
              avatarColor: 'bg-blue-100 text-blue-700'
            },
            priority: 'medium',
            isOverdue: false,
            daysPastDue: 0,
            dueDate: new Date(Date.now() + 1000 * 60 * 60 * 24 * 5).toISOString(), // 5 days from now
            invitedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(), // 2 days ago
            createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString()
          },
          {
            id: '3',
            cycle: {
              id: 'cycle-3',
              name: 'Leadership Assessment',
              description: 'Annual leadership evaluation',
              type: 'Leadership Review'
            },
            target: {
              id: 'target-3',
              name: 'Emily Zhang',
              role: 'Team Lead',
              avatar: null,
              initials: 'EZ',
              avatarColor: 'bg-green-100 text-green-700'
            },
            priority: 'low',
            isOverdue: false,
            daysPastDue: 0,
            dueDate: new Date(Date.now() + 1000 * 60 * 60 * 24 * 14).toISOString(), // 14 days from now
            invitedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 1).toISOString(), // 1 day ago
            createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 1).toISOString()
          }
        ]
      };
    },
    enabled: isAuthenticated && !!user?.id,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  })
}

// One-on-One Sessions Hooks
export const useOneOnOneSessions = (params?: Record<string, unknown>) => {
  const isAuthenticated = useIsAuthenticated();
  const { user } = useAuth();

  return useQuery({
    queryKey: ['oneOnOneSessions', user?.id, JSON.stringify(params ?? {})],
    queryFn: async () => {
      const response = await api.get('/one-on-ones', {
        params: {
          page: 1,
          limit: 10,
          ...(params ?? {}),
        },
      });

      if (response && typeof response === 'object' && 'data' in response) {
        return response;
      }

      return {
        success: true,
        data: Array.isArray(response) ? response : [],
      };
    },
    enabled: isAuthenticated && !!user?.id,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  })
}

export const useActionItems = (params?: Record<string, unknown>) => {
  const isAuthenticated = useIsAuthenticated();
  const { user } = useAuth();

  return useQuery({
    queryKey: ['actionItems', user?.id, JSON.stringify(params ?? {})],
    queryFn: async () => {
      const response = await api.get('/one-on-ones/action-items', {
        params: {
          ...(params ?? {}),
        },
      });

      if (response && typeof response === 'object' && 'data' in response) {
        return response;
      }

      return {
        success: true,
        data: Array.isArray(response) ? response : [],
      };
    },
    enabled: isAuthenticated && !!user?.id,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  })
}

// Organization Members Hooks
export const useOrganizationMembers = (organizationId?: string, params?: any, options?: any) => {
  const isAuthenticated = useIsAuthenticated();
  const { user } = useAuth();

  return useQuery({
    queryKey: ['organizationMembers', organizationId, params],
    queryFn: async () => {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      const { api } = await import('@/lib/api');
      return await api.get(`/organizations/${organizationId}/members`, { params });
    },
    enabled: isAuthenticated && !!organizationId && (options?.enabled !== false),
    retry: options?.retry !== undefined ? options.retry : false,
    refetchOnWindowFocus: options?.refetchOnWindowFocus !== undefined ? options.refetchOnWindowFocus : false,
    refetchOnMount: options?.refetchOnMount !== undefined ? options.refetchOnMount : true,
  })
}

export const useOrganizationUsage = (organizationId?: string) => {
  const isAuthenticated = useIsAuthenticated();
  const { user } = useAuth();

  return useQuery({
    queryKey: ['organizationUsage', organizationId],
    queryFn: async () => {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      return await api.get(`/organizations/${organizationId}/usage`);
    },
    enabled: isAuthenticated && !!organizationId && !!user?.id,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export const useTeams = (params?: any) => {
  const isAuthenticated = useIsAuthenticated();
  const { user } = useAuth();

  return useQuery({
    queryKey: ['teams', params],
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (params?.includeMembers) {
        queryParams.append('includeMembers', 'true');
      }
      if (params?.memberOfTeams) {
        queryParams.append('memberOfTeams', 'true');
      }

      const response = await api.get(`/teams?${queryParams.toString()}`);

      if (response && Array.isArray((response as any).data)) {
        return {
          ...(response as Record<string, unknown>),
          teams: (response as any).data,
        };
      }

      if (Array.isArray(response)) {
        return {
          success: true,
          data: response,
          teams: response,
        };
      }

      return response;
    },
    enabled: isAuthenticated && !!user?.id,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  })
}

export const useTeam = (teamId?: string) => {
  const isAuthenticated = useIsAuthenticated();
  const { user } = useAuth();

  return useQuery({
    queryKey: ['team', teamId],
    queryFn: async () => {
      if (!teamId) {
        throw new Error('Team ID is required');
      }

      const response = await api.get(`/teams/${teamId}`);
      return response;
    },
    enabled: isAuthenticated && !!user?.id && !!teamId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  })
}

export const useTeamAnalytics = () => {
  const isAuthenticated = useIsAuthenticated();
  const { user } = useAuth();

  return useQuery({
    queryKey: ['teamAnalytics'],
    queryFn: async () => {
      // For now return mock analytics data - can be connected to API later
      return {
        success: true,
        data: {
          overview: {
            totalTeams: 4,
            totalMembers: 31,
            teamLeaders: 4,
            temperature: "8.2"
          },
          teams: []
        }
      };
    },
    enabled: isAuthenticated && !!user?.id,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  })
}

type CreateTeamPayload = {
  name: string;
  description?: string;
  managerId?: string;
  leadId?: string;
  members?: string[];
};

export const useCreateTeam = (
  options?: UseMutationOptions<any, unknown, CreateTeamPayload, unknown>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (teamData: CreateTeamPayload) => {
      return await api.post('/teams', teamData);
    },
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      options?.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      options?.onError?.(error, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      options?.onSettled?.(data, error, variables, context);
    },
  });
};

export const useUpdateTeam = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; name?: string; description?: string; memberIds?: string[]; managerId?: string }) => {
      return await api.patch(`/teams/${id}`, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });
};

export const useDeleteTeam = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (teamId: string) => {
      return await api.delete(`/teams/${teamId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });
};

export const useAddTeamMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ teamId, userId, role }: { teamId: string; userId: string; role: 'MANAGER' | 'TEAM_LEAD' | 'MEMBER' }) => {
      return await api.post('/teams/members', {
        teamId,
        userId,
        role,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['team', variables.teamId] });
    },
  });
};

export const useRemoveTeamMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ teamId, userId }: { teamId: string; userId: string }) => {
      return await api.delete('/teams/members', {
        body: JSON.stringify({ teamId, userId }),
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['team', variables.teamId] });
    },
  });
};

export const useFeedbackTemplatesFromApi = (params?: { includeInactive?: boolean }) => {
  const isAuthenticated = useIsAuthenticated();
  const { user } = useAuth();

  return useQuery({
    queryKey: ['feedbackTemplates', params],
    queryFn: async () => {
      const response = await api.get('/feedback/templates', {
        params: {
          templateType: 'feedback',
          includeInactive: params?.includeInactive ?? false,
          includeQuestions: true,
        },
      });
      return response;
    },
    enabled: isAuthenticated && !!user?.id,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

// Template Hooks

type TemplateQuestionType = 'text' | 'rating' | 'multiple-choice' | 'multiple_choice'

interface TemplateQuestionEntity {
  id: string
  text: string
  type: TemplateQuestionType
  required: boolean
  options: string[]
}

interface TemplateEntity {
  id: string
  name: string
  description?: string
  templateType: 'feedback' | 'one_on_one'
  category: string
  questionCount: number
  usageCount: number
  isDefault: boolean
  creator: { id: string; firstName: string; lastName: string } | null
  questions: TemplateQuestionEntity[]
  createdAt: string
  updatedAt: string
}

interface TemplateQuestionInput {
  id?: string
  text: string
  type: TemplateQuestionType
  required?: boolean
  options?: string[]
}

interface TemplateCreateInput {
  id?: string
  name: string
  description?: string
  templateType: 'feedback' | 'one_on_one'
  category: string
  questions: TemplateQuestionInput[]
}

interface TemplateUpdateInput {
  id: string
  name?: string
  description?: string
  templateType?: 'feedback' | 'one_on_one'
  category?: string
  questions?: TemplateQuestionInput[]
}

type AuthUser = ReturnType<typeof useAuth>['user']

const templateStore = new Map<string, TemplateEntity>()
const deletedTemplateIds = new Set<string>()
const TEMPLATE_STORE_VERSION = 'v1'
const TEMPLATE_STORE_KEY_PREFIX = 'gathio-template-store'
const isBrowser = typeof window !== 'undefined'
let activeStoreKey: string | null = null
let hydratedStoreKey: string | null = null

const generateId = (prefix: string) => `${prefix}-${Math.random().toString(36).slice(2, 10)}`

const ensureCreator = (user: AuthUser, fallback?: TemplateEntity['creator']): TemplateEntity['creator'] => {
  if (fallback !== undefined && fallback !== null) return fallback
  if (!user) {
    return { id: 'current-user', firstName: 'Current', lastName: 'User' }
  }

  const inferredFirst = user.firstName || user.name?.split(' ')[0] || 'Current'
  const inferredLast = user.lastName || user.name?.split(' ')[1] || 'User'

  return {
    id: user.id,
    firstName: inferredFirst,
    lastName: inferredLast,
  }
}

const normalizeQuestions = (
  questions: TemplateQuestionInput[] | undefined,
  existing?: TemplateQuestionEntity[]
): TemplateQuestionEntity[] => {
  const source = questions ?? existing ?? []
  return source.map((question) => {
    const existingMatch = question.id ? existing?.find((q) => q.id === question.id) : undefined
    const id = question.id ?? existingMatch?.id ?? generateId('question')
    const normalizedType =
      question.type === 'multiple_choice'
        ? 'multiple-choice'
        : question.type ?? existingMatch?.type ?? 'text'
    const options =
      normalizedType === 'multiple-choice'
        ? [...(question.options ?? existingMatch?.options ?? [])]
        : []

    return {
      id,
      text: question.text,
      type: normalizedType,
      required: question.required ?? existingMatch?.required ?? false,
      options,
    }
  })
}

const convertTemplate = (template: any): TemplateEntity => ({
  ...template,
  questionCount: Array.isArray(template.questions) ? template.questions.length : 0,
  questions: Array.isArray(template.questions)
    ? template.questions.map((question: any) => ({
        id: question.id ?? generateId('question'),
        text: question.text,
        type: (question.type === 'multiple_choice'
          ? 'multiple-choice'
          : question.type ?? 'text') as TemplateQuestionType,
        required: question.required ?? false,
        options:
          (question.type === 'multiple_choice' || question.type === 'multiple-choice') &&
          Array.isArray(question.options)
            ? [...question.options]
            : [],
      }))
    : [],
})

const getDefaultTemplateCollections = (user: AuthUser) => {
  const nameParts = user?.name?.split(' ') ?? []
  const defaultCreator = user
    ? {
        id: user.id,
        firstName: user.firstName || nameParts[0] || 'Current',
        lastName: user.lastName || nameParts[1] || 'User',
      }
    : null

  const feedbackTemplates = [
    {
      id: 'template-1',
      name: 'Quarterly Performance Review',
      description: 'Comprehensive quarterly review template',
      templateType: 'feedback',
      category: 'performance',
      questionCount: 8,
      usageCount: 12,
      isDefault: true,
      creator: null,
      questions: [
        { id: 'q1', text: 'What are your key accomplishments this quarter?', type: 'text' },
        { id: 'q2', text: 'What challenges did you face?', type: 'text' },
        { id: 'q3', text: 'Rate your overall performance', type: 'rating' },
        { id: 'q4', text: 'What skills would you like to develop?', type: 'text' },
        { id: 'q5', text: 'How satisfied are you with your workload?', type: 'rating' },
        { id: 'q6', text: 'What support do you need from your manager?', type: 'text' },
        { id: 'q7', text: 'What are your goals for next quarter?', type: 'text' },
        { id: 'q8', text: 'Additional comments', type: 'text' },
      ],
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 30).toISOString(),
      updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 10).toISOString(),
    },
    {
      id: 'template-2',
      name: '360 Degree Feedback',
      description: 'Multi-source feedback template',
      templateType: 'feedback',
      category: 'assessment',
      questionCount: 6,
      usageCount: 8,
      isDefault: false,
      creator: defaultCreator,
      questions: [
        { id: 'q1', text: 'How effectively does this person communicate?', type: 'rating' },
        { id: 'q2', text: 'Rate their technical skills', type: 'rating' },
        { id: 'q3', text: 'How well do they collaborate?', type: 'rating' },
        { id: 'q4', text: 'What are their key strengths?', type: 'text' },
        { id: 'q5', text: 'Areas for improvement?', type: 'text' },
        { id: 'q6', text: 'Would you recommend working with them?', type: 'rating' },
      ],
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 45).toISOString(),
      updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 15).toISOString(),
    },
    {
      id: 'template-3',
      name: 'Project Retrospective',
      description: 'Capture learnings from completed projects',
      templateType: 'feedback',
      category: 'project',
      questionCount: 7,
      usageCount: 5,
      isDefault: false,
      creator: defaultCreator,
      questions: [
        { id: 'q1', text: 'What went well?', type: 'text' },
        { id: 'q2', text: 'What could have gone better?', type: 'text' },
        { id: 'q3', text: 'Were goals and timelines met?', type: 'rating' },
        { id: 'q4', text: 'How effective was communication?', type: 'rating' },
        { id: 'q5', text: 'Key learnings?', type: 'text' },
        { id: 'q6', text: 'Suggestions for next project?', type: 'text' },
        { id: 'q7', text: 'Overall project satisfaction', type: 'rating' },
      ],
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 60).toISOString(),
      updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5).toISOString(),
    },
    {
      id: 'template-4',
      name: 'New Hire Feedback',
      description: 'Gather insights from recent hires',
      templateType: 'feedback',
      category: 'onboarding',
      questionCount: 5,
      usageCount: 4,
      isDefault: true,
      creator: null,
      questions: [
        { id: 'q1', text: 'How was your onboarding experience?', type: 'rating' },
        { id: 'q2', text: 'Were expectations clear?', type: 'rating' },
        { id: 'q3', text: 'What improvements would you suggest?', type: 'text' },
        { id: 'q4', text: 'Do you feel supported by your team?', type: 'rating' },
        { id: 'q5', text: 'Any additional feedback?', type: 'text' },
      ],
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 20).toISOString(),
      updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(),
    },
  ]

  const oneOnOneTemplates = [
    {
      id: 'template-oneonone-1',
      name: 'Weekly Check-in',
      description: 'Structured weekly 1-on-1 conversation',
      templateType: 'one_on_one',
      category: 'weekly',
      questionCount: 6,
      usageCount: 25,
      isDefault: true,
      creator: null,
      questions: [
        { id: 'q1', text: 'How are you feeling about your work this week?', type: 'text' },
        { id: 'q2', text: 'What are your top priorities?', type: 'text' },
        { id: 'q3', text: 'Any blockers or challenges?', type: 'text' },
        { id: 'q4', text: 'What support do you need?', type: 'text' },
        { id: 'q5', text: 'Career development topics', type: 'text' },
        { id: 'q6', text: 'Feedback for me as your manager', type: 'text' },
      ],
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 45).toISOString(),
      updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7).toISOString(),
    },
    {
      id: 'template-oneonone-2',
      name: 'Career Development',
      description: 'Focus on career growth and development',
      templateType: 'one_on_one',
      category: 'development',
      questionCount: 7,
      usageCount: 10,
      isDefault: false,
      creator: defaultCreator,
      questions: [
        { id: 'q1', text: 'What are your short-term career goals?', type: 'text' },
        { id: 'q2', text: 'What skills do you want to develop?', type: 'text' },
        { id: 'q3', text: 'What opportunities interest you?', type: 'text' },
        { id: 'q4', text: 'How can I support your growth?', type: 'text' },
        { id: 'q5', text: 'What training would be helpful?', type: 'text' },
        { id: 'q6', text: 'Rate your job satisfaction', type: 'rating' },
        { id: 'q7', text: 'Additional thoughts on your career', type: 'text' },
      ],
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 25).toISOString(),
      updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(),
    },
  ]

  return {
    feedback: feedbackTemplates.map(convertTemplate),
    oneOnOne: oneOnOneTemplates.map(convertTemplate),
  }
}

const getStoreKey = (user: AuthUser) => {
  const userKey = user?.id ? `user-${user.id}` : 'guest'
  return `${TEMPLATE_STORE_KEY_PREFIX}-${userKey}-${TEMPLATE_STORE_VERSION}`
}

interface TemplateStoreSnapshot {
  templates: TemplateEntity[]
  deletedIds: string[]
}

const persistTemplateStore = () => {
  if (!isBrowser || !activeStoreKey) return

  const snapshot: TemplateStoreSnapshot = {
    templates: Array.from(templateStore.values()),
    deletedIds: Array.from(deletedTemplateIds),
  }

  try {
    window.localStorage.setItem(activeStoreKey, JSON.stringify(snapshot))
  } catch (error) {
    console.warn('Unable to persist template store', error)
  }
}

const hydrateTemplateStore = (user: AuthUser) => {
  if (!isBrowser) return

  const storeKey = getStoreKey(user)
  if (hydratedStoreKey === storeKey) {
    activeStoreKey = storeKey
    return
  }

  activeStoreKey = storeKey
  hydratedStoreKey = storeKey

  templateStore.clear()
  deletedTemplateIds.clear()

  const raw = window.localStorage.getItem(storeKey)
  if (!raw) return

  try {
    const parsed = JSON.parse(raw) as TemplateStoreSnapshot
    const templates = Array.isArray(parsed?.templates) ? parsed.templates : []
    const deletedIds = Array.isArray(parsed?.deletedIds) ? parsed.deletedIds : []

    templates.forEach((template) => {
      const converted = convertTemplate(template)
      templateStore.set(converted.id, converted)
    })

    deletedIds.forEach((id) => deletedTemplateIds.add(id))
  } catch (error) {
    console.warn('Unable to hydrate template store', error)
  }
}

const findTemplateById = (id: string, user: AuthUser): TemplateEntity | undefined => {
  const override = templateStore.get(id)
  if (override) return override

  const { feedback, oneOnOne } = getDefaultTemplateCollections(user)
  return [...feedback, ...oneOnOne].find((template) => template.id === id)
}

const addOrUpdateTemplate = (template: TemplateEntity) => {
  templateStore.set(template.id, {
    ...template,
    questions: template.questions.map((question) => ({
      ...question,
      options: [...question.options],
    })),
    updatedAt: template.updatedAt,
  })
  deletedTemplateIds.delete(template.id)
  persistTemplateStore()
}

const removeTemplate = (templateId: string) => {
  templateStore.delete(templateId)
  deletedTemplateIds.add(templateId)
  persistTemplateStore()
}

const buildTemplateForCreate = (
  input: TemplateCreateInput,
  user: AuthUser,
  overrides?: Partial<Pick<TemplateEntity, 'id' | 'isDefault' | 'usageCount' | 'creator' | 'createdAt'>>
): TemplateEntity => {
  const now = new Date().toISOString()
  const questions = normalizeQuestions(input.questions)

  return {
    id: overrides?.id ?? generateId('template'),
    name: input.name,
    description: input.description,
    templateType: input.templateType,
    category: input.category,
    questionCount: questions.length,
    usageCount: overrides?.usageCount ?? 0,
    isDefault: overrides?.isDefault ?? false,
    creator: ensureCreator(user, overrides?.creator),
    questions,
    createdAt: overrides?.createdAt ?? now,
    updatedAt: now,
  }
}

const buildTemplateForUpdate = (
  input: TemplateUpdateInput,
  base: TemplateEntity,
  user: AuthUser
): TemplateEntity => {
  const now = new Date().toISOString()
  const questions = normalizeQuestions(input.questions, base.questions)

  return {
    ...base,
    name: input.name ?? base.name,
    description: input.description ?? base.description,
    templateType: input.templateType ?? base.templateType,
    category: input.category ?? base.category,
    questions,
    questionCount: questions.length,
    creator: ensureCreator(user, base.creator),
    updatedAt: now,
  }
}

export const useTemplates = (params?: { type?: 'feedback' | 'one_on_one'; includeDefaults?: boolean }) => {
  const isAuthenticated = useIsAuthenticated();
  const { user } = useAuth();

  hydrateTemplateStore(user);

  return useQuery({
    queryKey: ['templates', params],
    queryFn: async () => {
      const { feedback, oneOnOne } = getDefaultTemplateCollections(user);

      let templates: TemplateEntity[] = []
      if (!params?.type || params.type === 'feedback') {
        templates = templates.concat(feedback)
      }
      if (!params?.type || params.type === 'one_on_one') {
        templates = templates.concat(oneOnOne)
      }

      if (params?.includeDefaults === false) {
        templates = templates.filter((template) => !template.isDefault)
      }

      const defaultsById = new Map(templates.map((template) => [template.id, template]))

      const mergedDefaults = templates
        .filter((template) => !deletedTemplateIds.has(template.id))
        .map((template) => templateStore.get(template.id) ?? template)

      const customTemplates = Array.from(templateStore.values()).filter((template) => {
        if (deletedTemplateIds.has(template.id)) return false
        if (defaultsById.has(template.id)) return false
        if (params?.type && template.templateType !== params.type) return false
        return params?.includeDefaults === false ? !template.isDefault : true
      })

      const result = [...mergedDefaults, ...customTemplates]
      result.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())

      return {
        success: true,
        data: result,
      }
    },
    enabled: isAuthenticated && !!user?.id,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  })
}

export const useTemplate = (templateId: string, options?: any) => {
  const isAuthenticated = useIsAuthenticated();
  const { user } = useAuth();

  hydrateTemplateStore(user);

  return useQuery({
    queryKey: ['template', templateId],
    queryFn: async () => {
      const template = findTemplateById(templateId, user);

      if (!template) {
        throw new Error('Template not found');
      }

      return {
        success: true,
        data: template,
      };
    },
    enabled: isAuthenticated && !!templateId && (options?.enabled !== false),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  })
}

// Template mutation hooks
export const useCreateTemplate = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (templateData: TemplateCreateInput) => {
      hydrateTemplateStore(user);

      const template = buildTemplateForCreate(templateData, user);

      await new Promise((resolve) => setTimeout(resolve, 300));

      addOrUpdateTemplate(template);

      return {
        success: true,
        data: template,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });
};

export const useUpdateTemplate = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (templateData: TemplateUpdateInput) => {
      hydrateTemplateStore(user);

      const baseTemplate = findTemplateById(templateData.id, user);

      if (!baseTemplate) {
        throw new Error('Template not found');
      }

      const template = buildTemplateForUpdate(templateData, baseTemplate, user);

      await new Promise((resolve) => setTimeout(resolve, 300));

      addOrUpdateTemplate(template);

      return {
        success: true,
        data: template,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });
};

export const useDeleteTemplate = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (templateId: string) => {
      hydrateTemplateStore(user);

      await new Promise((resolve) => setTimeout(resolve, 200));

      removeTemplate(templateId);

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });
};

export const useDuplicateTemplate = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, name, description }: { id: string; name: string; description?: string }) => {
      hydrateTemplateStore(user);

      const baseTemplate = findTemplateById(id, user);

      if (!baseTemplate) {
        throw new Error('Template not found');
      }

      const duplicateInput: TemplateCreateInput = {
        name,
        description: description ?? baseTemplate.description,
        templateType: baseTemplate.templateType,
        category: baseTemplate.category,
        questions: baseTemplate.questions.map((question) => ({
          text: question.text,
          type: question.type,
          required: question.required,
          options: [...question.options],
        })),
      };

      const duplicateTemplate = buildTemplateForCreate(duplicateInput, user);

      await new Promise((resolve) => setTimeout(resolve, 300));

      addOrUpdateTemplate(duplicateTemplate);

      return {
        success: true,
        data: duplicateTemplate,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });
};

// ============================================================================
// ORGANIZATION HOOKS
// ============================================================================

export const useOrganization = (organizationId?: string) => {
  const isAuthenticated = useIsAuthenticated();
  const { user } = useAuth();

  return useQuery({
    queryKey: ['organization', organizationId],
    queryFn: async () => {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      const { api } = await import('@/lib/api');
      return await api.get(`/organizations/${organizationId}`);
    },
    enabled: isAuthenticated && !!organizationId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  })
}

// Organization mutation hooks
type UpdateOrganizationPayload = Record<string, unknown> & { organizationId?: string };

export const useUpdateOrganization = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationKey: ['update-organization', user?.currentOrganizationId],
    mutationFn: async (payload: UpdateOrganizationPayload) => {
      const { organizationId, ...rest } = payload;
      const resolvedOrganizationId = (organizationId ?? user?.currentOrganizationId) as string | undefined;

      if (!resolvedOrganizationId) {
        throw new Error('Organization ID is required to update organization settings.');
      }

      const allowedKeys = new Set([
        'name',
        'description',
        'industry',
        'companySize',
        'timeZone',
        'website',
        'plan',
        'anonymousFeedback',
      ]);

      const requestBody: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(rest)) {
        if (allowedKeys.has(key) && value !== undefined) {
          requestBody[key] = value;
        }
      }

      if (Object.keys(requestBody).length === 0) {
        return {
          success: true,
          data: null,
          skipped: true,
          message: 'No supported organization fields provided.',
        } as { success: true; data: null; skipped: true; message: string };
      }

      return api.put(`/organizations/${resolvedOrganizationId}`, requestBody);
    },
    onSuccess: (_response, variables) => {
      const resolvedOrganizationId = (variables.organizationId ?? user?.currentOrganizationId) as string | undefined;

      if (resolvedOrganizationId) {
        queryClient.invalidateQueries({ queryKey: ['organization', resolvedOrganizationId] });
        queryClient.invalidateQueries({ queryKey: ['organizationMembers', resolvedOrganizationId] });
      }
    },
  });
}

type CreateOrganizationPayload = {
  name: string
  plan: string
  description?: string
  industry?: string
  companySize?: string
  website?: string
}

type CreateOrganizationResponse = {
  success: boolean
  data?: {
    id: string
    name: string
    description?: string | null
    industry?: string | null
    companySize?: string | null
    website?: string | null
    plan: string
    role: string
    createdAt: string
  }
  message?: string
  error?: string
  code?: string
  paymentLink?: string
  action?: string
  organizationData?: unknown
}

export type CreateOrganizationError = Error & {
  code?: string
  status?: number
  paymentLink?: string
  action?: string
  organizationData?: unknown
}

export const useCreateOrganization = (
  options?: UseMutationOptions<CreateOrganizationResponse, CreateOrganizationError, CreateOrganizationPayload, unknown>
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: CreateOrganizationPayload) => {
      try {
        return await api.post<CreateOrganizationResponse>('/organizations', payload)
      } catch (error) {
        if (error instanceof ApiError) {
          let parsed: any = null
          try {
            parsed = JSON.parse(error.message)
          } catch (parseError) {
            parsed = null
          }

          const enrichedError = new Error(
            parsed?.error ?? parsed?.message ?? 'Failed to create organization',
          ) as CreateOrganizationError

          enrichedError.code = parsed?.code ?? undefined
          enrichedError.status = error.status
          enrichedError.paymentLink = parsed?.paymentLink ?? undefined
          enrichedError.action = parsed?.action ?? undefined
          enrichedError.organizationData = parsed?.organizationData ?? undefined

          throw enrichedError
        }

        throw error as CreateOrganizationError
      }
    },
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] })
      options?.onSuccess?.(data, variables, context)
    },
    onError: (error, variables, context) => {
      options?.onError?.(error, variables, context)
    },
    onSettled: (data, error, variables, context) => {
      options?.onSettled?.(data, error, variables, context)
    },
  })
}

export const useUploadOrganizationLogo = () => {
  const queryClient = useQueryClient();
  const { user, updateUser } = useAuth();

  return useMutation({
    mutationKey: ['upload-organization-logo'],
    mutationFn: async ({
      organizationId,
      file,
    }: {
      organizationId: string;
      file: File;
    }) => {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      const formData = new FormData();
      formData.append('logo', file);
      formData.append('file', file);

      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${getApiBaseUrl()}/organizations/${organizationId}/logo`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to upload organization logo');
      }

      return response.json() as Promise<{
        success: boolean;
        data?: { url?: string | null };
        message?: string;
      }>;
    },
    onSuccess: (data, variables) => {
      const normalizedUrl = resolveAssetUrl(data?.data?.url ?? null);

      if (user) {
        const updatedOrganizations = user.organizations.map((org) =>
          org.id === variables.organizationId
            ? {
                ...org,
                avatar: normalizedUrl ?? undefined,
              }
            : org,
        );

        updateUser({ organizations: updatedOrganizations });
      }

      queryClient.invalidateQueries({ queryKey: ['organization', variables.organizationId] });
      queryClient.invalidateQueries({ queryKey: ['organizationMembers', variables.organizationId] });
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
};

export const useRemoveOrganizationLogo = () => {
  const queryClient = useQueryClient();
  const { user, updateUser } = useAuth();

  return useMutation({
    mutationKey: ['remove-organization-logo'],
    mutationFn: async ({ organizationId }: { organizationId: string }) => {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${getApiBaseUrl()}/organizations/${organizationId}/logo`, {
        method: 'DELETE',
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to remove organization logo');
      }

      return response.json() as Promise<{ success: boolean; data?: { url?: string | null }; message?: string }>;
    },
    onSuccess: (_data, variables) => {
      if (user) {
        const updatedOrganizations = user.organizations.map((org) =>
          org.id === variables.organizationId
            ? {
                ...org,
                avatar: undefined,
              }
            : org,
        );

        updateUser({ organizations: updatedOrganizations });
      }

      queryClient.invalidateQueries({ queryKey: ['organization', variables.organizationId] });
      queryClient.invalidateQueries({ queryKey: ['organizationMembers', variables.organizationId] });
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
};

// Billing hooks
export const useCreateBillingPortal = () => {
  return {
    mutateAsync: async ({ organizationId }: { organizationId?: string | null }) => {
      if (!organizationId) {
        throw new Error('Organization ID is required to open billing portal');
      }

      return api.post('/billing/create-portal', { organizationId });
    },
  };
};

export const useCreatePaymentLink = () => {
  return {
    mutateAsync: async ({ planId, organizationId }: { planId?: string | null; organizationId?: string | null }) => {
      if (!planId) {
        throw new Error('Plan ID is required to create payment link');
      }

      if (!organizationId) {
        throw new Error('Organization ID is required to create payment link');
      }

      return api.post('/billing/payment-link', {
        planId,
        organizationId,
      });
    },
  };
};

export const useCreateCheckoutSession = () => {
  return {
    mutateAsync: async ({ planId, organizationId, usePaymentLink }: { planId?: string | null; organizationId?: string | null; usePaymentLink?: boolean }) => {
      if (!planId) {
        throw new Error('Plan ID is required to create checkout session');
      }

      if (!organizationId) {
        throw new Error('Organization ID is required to create checkout session');
      }

      return api.post('/billing/create-checkout', {
        planId,
        organizationId,
        usePaymentLink: usePaymentLink ?? false,
      });
    },
  };
};

// ============================================================================
// AUDIT TRAIL HOOKS
// ============================================================================

type AuditTrailDateInput = Date | string | null | undefined

export interface AuditTrailFilters {
  search?: string
  action?: string
  entityType?: string
  severity?: string
  user?: string
  ipAddress?: string
  userAgent?: string
  success?: boolean
  dateRange?: {
    from?: AuditTrailDateInput
    to?: AuditTrailDateInput
  }
  from?: AuditTrailDateInput
  to?: AuditTrailDateInput
  enabled?: boolean
}

export interface AuditLogEntry {
  id: number | string
  organizationId?: string | null
  userId?: string | null
  userEmail?: string | null
  userName: string
  action: string
  entityType: string
  entityId?: string | null
  oldValues?: Record<string, unknown>
  newValues?: Record<string, unknown>
  ipAddress?: string | null
  userAgent?: string | null
  sessionId?: string | null
  requestId?: string | null
  resourcePath?: string | null
  httpMethod?: string | null
  responseTimeMs?: number | null
  auditSource?: string | null
  severity: string
  success?: boolean | null
  dataClassification?: string | null
  metadata?: Record<string, unknown>
  description?: string
  location?: string | null
  details?: Record<string, unknown>
  timestamp: string
}

export interface AuditFilterUserOption {
  id: string
  name: string
  email: string | null
}

export interface AuditTrailFilterOptions {
  users: AuditFilterUserOption[]
  actions: string[]
  entityTypes: string[]
  severities: string[]
}

interface AuditTrailApiResponse {
  success: boolean
  data: AuditLogEntry[]
  pagination: {
    currentPage: number
    totalPages: number
    totalItems: number
    itemsPerPage: number
    hasMore: boolean
  }
  filters: AuditTrailFilterOptions
}

const AUDIT_ALLOWED_ROLES = new Set(['OWNER', 'ADMIN'])

function toIsoDate(value: AuditTrailDateInput): string | undefined {
  if (!value) return undefined
  if (value instanceof Date) return value.toISOString()
  if (typeof value === 'string') {
    const parsed = new Date(value)
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString()
    }
  }
  return undefined
}

function normalizeFilterValue(value?: string | null): string | undefined {
  if (!value) return undefined
  if (value.toLowerCase() === 'all') return undefined
  return value
}

function buildAuditQueryParams(
  page: number,
  itemsPerPage: number,
  filters?: Omit<AuditTrailFilters, 'enabled'>,
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {
    page,
    limit: itemsPerPage,
  }

  if (!filters) {
    return params
  }

  const search = filters.search?.trim()
  if (search) params.search = search

  const action = normalizeFilterValue(filters.action)
  if (action) params.action = action.toUpperCase()

  const entityType = normalizeFilterValue(filters.entityType)
  if (entityType) params.entityType = entityType.toUpperCase()

  const severity = normalizeFilterValue(filters.severity)
  if (severity) params.severity = severity.toUpperCase()

  const user = normalizeFilterValue(filters.user)
  if (user) params.user = user

  const ipAddress = filters.ipAddress?.trim()
  if (ipAddress) params.ipAddress = ipAddress

  const userAgent = filters.userAgent?.trim()
  if (userAgent) params.userAgent = userAgent

  if (typeof filters.success === 'boolean') {
    params.success = filters.success
  }

  const from = toIsoDate(filters.dateRange?.from ?? filters.from)
  if (from) params.from = from

  const to = toIsoDate(filters.dateRange?.to ?? filters.to)
  if (to) params.to = to

  return params
}

function canAccessAuditTrail(user: ReturnType<typeof useAuth>['user']): boolean {
  const role = (user?.role ?? user?.organizationRole ?? '').toUpperCase()
  return AUDIT_ALLOWED_ROLES.has(role)
}

export const useAuditTrail = (filters?: AuditTrailFilters) => {
  return useAuditTrailPaginated(1, 100, filters)
}

export const useAuditTrailPaginated = (
  page: number,
  itemsPerPage: number,
  filters?: AuditTrailFilters,
) => {
  const isAuthenticated = useIsAuthenticated()
  const { user } = useAuth()

  const { enabled: enabledOverride, ...filterValues } = filters ?? {}
  const queryParams = buildAuditQueryParams(page, itemsPerPage, filterValues)

  return useQuery({
    queryKey: ['audit-trail-paginated', queryParams],
    queryFn: async () => {
      const response = await api.get<AuditTrailApiResponse>('/audit/logs', { params: queryParams })
      if (!response.success) {
        throw new Error('Failed to fetch audit trail')
      }
      return response
    },
    enabled: (enabledOverride ?? true) && isAuthenticated && canAccessAuditTrail(user),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: 1000 * 30,
  })
}

export const useExportAuditTrail = () => {
  return {
    mutateAsync: async (data: any) => {
      console.log('Exporting audit trail:', data);
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Simulate export generation
      const exportData = {
        filename: `audit-trail-export-${new Date().toISOString().split('T')[0]}.csv`,
        downloadUrl: 'https://example.com/download/audit-export.csv',
        recordCount: 150,
        format: data.format || 'csv'
      };

      return {
        success: true,
        data: exportData
      };
    }
  };
}

// Email Management Hooks
export interface EmailLogFilters {
  emailType?: string
  status?: string
  recipient?: string
  startDate?: string
  endDate?: string
  search?: string
  limit?: number
}

interface EmailLog {
  id: string
  emailType: string
  recipientEmail: string
  recipientName: string | null
  senderEmail: string | null
  senderName: string | null
  emailSubject: string | null
  emailStatus: string
  sentAt: string | null
  errorMessage: string | null
  providerMessageId: string | null
  triggeredByUserId: string | null
  metadata: Record<string, unknown> | null
  relatedCycleTitle?: string | null
  relatedSessionTitle?: string | null
}

interface EmailLogsResponse {
  success: boolean
  data: {
    emails: EmailLog[]
    pagination: {
      currentPage: number
      totalPages: number
      total: number
      itemsPerPage: number
      hasMore: boolean
    }
  }
}

interface EmailAnalyticsResponse {
  success: boolean
  data: {
    overall: {
      total_emails: number
      total_sent: number
      total_failed: number
      last_7_days: number
      last_30_days: number
      unique_recipients: number
      success_rate: number
    }
    volumeByDay: Array<{ date: string; count: number; sent_count: number; failed_count: number }>
    byType: Array<{ email_type: string; count: number; sent_count: number; failed_count: number }>
    topRecipients: Array<{ recipient_email: string; recipient_name: string | null; email_count: number; sent_count: number; last_email_sent: string | null }>
    failureReasons: Array<{ error_type: string; count: number }>
  }
}

interface EmailExportResponse {
  success: boolean
  data: {
    recordCount: number
    returnedCount: number
    generatedAt: string
    filename: string
    emails: EmailLog[]
  }
}

const sanitizeFilterValue = (value?: string) => {
  if (!value) {
    return undefined
  }
  const trimmed = value.trim()
  if (trimmed.length === 0 || trimmed === 'all') {
    return undefined
  }
  return trimmed
}

const buildEmailLogParams = (filters?: EmailLogFilters) => {
  if (!filters) {
    return {}
  }

  const params: Record<string, string | number> = {}
  const emailType = sanitizeFilterValue(filters.emailType)
  if (emailType) {
    params.emailType = emailType
  }

  const status = sanitizeFilterValue(filters.status)
  if (status) {
    params.status = status
  }

  const recipient = sanitizeFilterValue(filters.recipient)
  if (recipient) {
    params.recipient = recipient
  }

  const startDate = sanitizeFilterValue(filters.startDate)
  if (startDate) {
    params.startDate = startDate
  }

  const endDate = sanitizeFilterValue(filters.endDate)
  if (endDate) {
    params.endDate = endDate
  }

  const search = sanitizeFilterValue(filters.search)
  if (search) {
    params.search = search
  }

  if (typeof filters.limit === 'number') {
    params.limit = filters.limit
  }

  return params
}

export const useEmailLogs = (filters?: EmailLogFilters) => {
  const isAuthenticated = useIsAuthenticated()
  const { user } = useAuth()

  return useQuery({
    queryKey: ['email-logs', filters],
    queryFn: async () => {
      const params = {
        page: 1,
        itemsPerPage: 100,
        ...buildEmailLogParams(filters),
      }

      const response = await api.get<EmailLogsResponse>('/email/logs', { params })

      if (!response?.success) {
        throw new Error('Failed to load email logs')
      }

      return response
    },
    enabled: isAuthenticated && !!user?.id,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  })
}

export const useEmailLogsPaginated = (page: number, itemsPerPage: number, filters?: EmailLogFilters) => {
  const isAuthenticated = useIsAuthenticated()
  const { user } = useAuth()

  return useQuery({
    queryKey: ['email-logs-paginated', page, itemsPerPage, filters],
    queryFn: async () => {
      const params = {
        page,
        itemsPerPage,
        ...buildEmailLogParams(filters),
      }

      const response = await api.get<EmailLogsResponse>('/email/logs', { params })

      if (!response?.success) {
        throw new Error('Failed to load email logs')
      }

      return response
    },
    enabled: isAuthenticated && !!user?.id,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  })
}

export const useEmailAnalytics = () => {
  const isAuthenticated = useIsAuthenticated()
  const { user } = useAuth()

  return useQuery({
    queryKey: ['email-analytics'],
    queryFn: async () => {
      const response = await api.get<EmailAnalyticsResponse>('/email/analytics')

      if (!response?.success) {
        throw new Error('Failed to load email analytics')
      }

      return response
    },
    enabled: isAuthenticated && !!user?.id,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  })
}

export const useExportEmailLogs = () => {
  return {
    mutateAsync: async (filters: EmailLogFilters = {}) => {
      const payload = {
        ...buildEmailLogParams(filters),
        limit: typeof filters.limit === 'number' ? filters.limit : undefined,
      }

      const response = await api.post<EmailExportResponse>('/email/export', payload)

      if (!response?.success) {
        throw new Error('Failed to export email logs')
      }

      return response
    },
  }
}

// Profile Management Hooks
export const useProfileData = () => {
  const isAuthenticated = useIsAuthenticated();
  const { user } = useAuth();

  const getApiBaseUrl = () => {
    const base = import.meta.env.VITE_API_URL || '/api';
    if (base.startsWith('http')) {
      return base.replace(/\/$/, '');
    }
    return `${window.location.origin}${base.replace(/\/$/, '')}`;
  };

  const normalizePhotoUrl = (photoUrl: string | null | undefined) => {
    if (!photoUrl) {
      return null;
    }
    if (photoUrl.startsWith('http://') || photoUrl.startsWith('https://')) {
      return photoUrl;
    }
    const base = getApiBaseUrl();
    const separator = photoUrl.startsWith('/') ? '' : '/';
    return `${base}${separator}${photoUrl}`;
  };

  return useQuery({
    queryKey: ['profile-data', user?.id],
    queryFn: async () => {
      const response = await api.get<{
        success: boolean;
        data: {
          id: string;
          email: string;
          firstName: string;
          lastName: string;
          avatar: string | null;
          jobTitle?: string | null;
          department?: string | null;
          phone?: string | null;
          bio?: string | null;
        };
      }>('/auth/me');

      if (!response?.success || !response.data) {
        throw new Error('Failed to load profile data');
      }

      const profile = response.data;

      return {
        id: profile.id,
        email: profile.email,
        firstName: profile.firstName,
        lastName: profile.lastName,
        photoUrl: normalizePhotoUrl(profile.avatar),
        jobTitle: profile.jobTitle ?? undefined,
        department: profile.department ?? undefined,
        phone: profile.phone ?? undefined,
        bio: profile.bio ?? undefined,
      };
    },
    enabled: isAuthenticated && !!user?.id,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  const { user, updateUser } = useAuth();

  const uploadAvatar = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${getApiBaseUrl()}/auth/upload-avatar`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to upload avatar');
    }

    return response.json() as Promise<{
      success: boolean;
      data?: { avatar?: string | null; user?: { avatar?: string | null } };
    }>;
  };

  return useMutation({
    mutationKey: ['update-profile', user?.id],
    mutationFn: async ({
      photoRemoved,
      photoFile,
      ...profileData
    }: {
      firstName: string;
      lastName: string;
      email: string;
      photoFile?: File;
      photoRemoved?: boolean;
    }) => {
      const profileResponse = await api.put<{
        success: boolean;
        data: {
          id: string;
          email: string;
          firstName: string;
          lastName: string;
          avatar: string | null;
        };
      }>('/auth/me', {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
      });

      if (!profileResponse?.success) {
        throw new Error('Failed to update profile');
      }

      let photoUrl = profileResponse.data.avatar ?? null;

      if (photoRemoved) {
        await api.delete('/auth/remove-avatar');
        photoUrl = null;
      }

      if (photoFile) {
        const uploadResponse = await uploadAvatar(photoFile);
        if (!uploadResponse.success) {
          throw new Error('Failed to upload avatar');
        }
        photoUrl = uploadResponse.data?.avatar ?? uploadResponse.data?.user?.avatar ?? photoUrl;
      }

      return {
        success: true,
        data: {
          firstName: profileResponse.data.firstName,
          lastName: profileResponse.data.lastName,
          email: profileResponse.data.email,
          photoUrl: resolveAssetUrl(photoUrl),
        },
      };
    },
    onSuccess: (response) => {
      if (!response?.data) {
        return;
      }

      updateUser({
        firstName: response.data.firstName,
        lastName: response.data.lastName,
        name: `${response.data.firstName} ${response.data.lastName}`.trim(),
        avatar: response.data.photoUrl ?? undefined,
      });

      queryClient.setQueryData(['profile-data', user?.id], (existing: any) => {
        if (!existing) {
          return response.data;
        }

        return {
          ...existing,
          ...response.data,
        };
      });

      queryClient.invalidateQueries({ queryKey: ['profile-data', user?.id] });
    },
  });
}

export const useChangePassword = () => {
  return useMutation({
    mutationKey: ['change-password'],
    mutationFn: async (passwordData: {
      currentPassword?: string;
      newPassword: string;
      firstTime?: boolean;
    }) => {
      const response = await api.post<{
        success: boolean;
        message: string;
      }>('/auth/change-password', passwordData);

      if (!response?.success) {
        throw new Error(response?.message || 'Failed to change password');
      }

      return response;
    },
  });
}

export const useDeactivateAccount = () => {
  return useMutation({
    mutationKey: ['deactivate-account'],
    mutationFn: async (data: {
      reason: string;
      feedback?: string;
    }) => {
      const response = await api.post<{
        success: boolean;
        message: string;
      }>('/auth/deactivate', data);

      if (!response?.success) {
        throw new Error(response?.message || 'Failed to deactivate account');
      }

      return response;
    },
  });
}

export const useDeleteAccount = () => {
  return useMutation({
    mutationKey: ['delete-account'],
    mutationFn: async (data: {
      password?: string;
      reason: string;
      feedback?: string;
      confirmText: string;
    }) => {
      const response = await api.post<{
        success: boolean;
        message: string;
      }>('/auth/delete-account', data);

      if (!response?.success) {
        throw new Error(response?.message || 'Failed to delete account');
      }

      return response;
    },
  });
}

// Reports and Analytics Hooks
export const useTeamHealthMetrics = (filters?: any) => {
  const isAuthenticated = useIsAuthenticated();
  const { user } = useAuth();

  return useQuery({
    queryKey: ['team-health-metrics', filters],
    queryFn: async () => {
      // Mock team health data
      const mockTeamHealthData = {
        overallHealth: 78,
        healthChange: 5,
        engagement: 82,
        engagementChange: 3,
        satisfaction: 76,
        satisfactionChange: -2,
        productivity: 85,
        productivityChange: 7,
        teams: [
          {
            id: 'team-1',
            name: 'Engineering Team',
            avatar: null,
            memberCount: 12,
            healthScore: 85,
            engagement: 88,
            satisfaction: 82,
            productivity: 90
          },
          {
            id: 'team-2',
            name: 'Product Team',
            avatar: null,
            memberCount: 8,
            healthScore: 79,
            engagement: 83,
            satisfaction: 78,
            productivity: 81
          },
          {
            id: 'team-3',
            name: 'Design Team',
            avatar: null,
            memberCount: 6,
            healthScore: 92,
            engagement: 95,
            satisfaction: 89,
            productivity: 88
          },
          {
            id: 'team-4',
            name: 'Marketing Team',
            avatar: null,
            memberCount: 10,
            healthScore: 71,
            engagement: 75,
            satisfaction: 69,
            productivity: 78
          }
        ]
      };

      return {
        success: true,
        data: mockTeamHealthData
      };
    },
    enabled: isAuthenticated && !!user?.id,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export const useFeedbackMetrics = (filters?: any) => {
  const isAuthenticated = useIsAuthenticated();
  const { user } = useAuth();

  return useQuery({
    queryKey: ['feedback-metrics', filters],
    queryFn: async () => {
      // Mock feedback metrics data
      const mockFeedbackData = {
        totalFeedback: 247,
        feedbackChange: 12,
        responseRate: 85,
        responseRateChange: 3,
        avgResponseTime: 18,
        responseTimeChange: -4,
        qualityScore: 89,
        qualityChange: 6,
        activeRequests: 23,
        activeChange: -8,
        completedRequests: 189,
        completedChange: 15,
        overdueRequests: 5,
        overdueChange: -2,
        feedbackTypes: [
          {
            name: 'Performance Review',
            count: 89,
            percentage: 36,
            color: 'blue'
          },
          {
            name: 'Project Feedback',
            count: 67,
            percentage: 27,
            color: 'green'
          },
          {
            name: '360 Feedback',
            count: 45,
            percentage: 18,
            color: 'purple'
          },
          {
            name: 'Skill Assessment',
            count: 32,
            percentage: 13,
            color: 'orange'
          },
          {
            name: 'Other',
            count: 14,
            percentage: 6,
            color: 'gray'
          }
        ]
      };

      return {
        success: true,
        data: mockFeedbackData
      };
    },
    enabled: isAuthenticated && !!user?.id,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export const usePerformanceMetrics = (filters?: any) => {
  const isAuthenticated = useIsAuthenticated();
  const { user } = useAuth();

  return useQuery({
    queryKey: ['performance-metrics', filters],
    queryFn: async () => {
      // Mock performance metrics data
      const mockPerformanceData = {
        goalCompletion: 82,
        goalCompletionChange: 8,
        projectDelivery: 89,
        deliveryChange: 4,
        performanceScore: 85,
        performanceChange: 6,
        growthRate: 12,
        growthChange: 3,
        topPerformers: [
          {
            id: 'user-1',
            firstName: 'Sarah',
            lastName: 'Johnson',
            title: 'Senior Product Manager',
            department: 'Product',
            avatar: null,
            score: 95
          },
          {
            id: 'user-2',
            firstName: 'Michael',
            lastName: 'Chen',
            title: 'Lead Engineer',
            department: 'Engineering',
            avatar: null,
            score: 92
          },
          {
            id: 'user-3',
            firstName: 'Emily',
            lastName: 'Rodriguez',
            title: 'UX Designer',
            department: 'Design',
            avatar: null,
            score: 90
          },
          {
            id: 'user-4',
            firstName: 'David',
            lastName: 'Kim',
            title: 'Marketing Manager',
            department: 'Marketing',
            avatar: null,
            score: 88
          },
          {
            id: 'user-5',
            firstName: 'Lisa',
            lastName: 'Thompson',
            title: 'Data Analyst',
            department: 'Analytics',
            avatar: null,
            score: 87
          }
        ]
      };

      return {
        success: true,
        data: mockPerformanceData
      };
    },
    enabled: isAuthenticated && !!user?.id,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export const useTeamMembers = (params?: any) => {
  const isAuthenticated = useIsAuthenticated();
  const { user } = useAuth();

  return useQuery({
    queryKey: ['team-members', params],
    queryFn: async () => {
      // Mock team members data - different from organization members
      // This represents members grouped by teams/departments
      const mockTeamMembers = [
        {
          id: 'team-1',
          name: 'Engineering Team',
          description: 'Software development and technical infrastructure',
          memberCount: 8,
          leadId: 'user-1',
          leadName: 'Sarah Johnson',
          members: [
            {
              id: 'user-1',
              firstName: 'Sarah',
              lastName: 'Johnson',
              email: 'sarah.johnson@company.com',
              title: 'Engineering Team Lead',
              department: 'Engineering',
              avatar: null,
              role: 'Team Lead',
              joinedAt: '2023-01-15T00:00:00.000Z',
              status: 'active'
            },
            {
              id: 'user-2',
              firstName: 'Michael',
              lastName: 'Chen',
              email: 'michael.chen@company.com',
              title: 'Senior Frontend Developer',
              department: 'Engineering',
              avatar: null,
              role: 'Senior Developer',
              joinedAt: '2023-02-01T00:00:00.000Z',
              status: 'active'
            },
            {
              id: 'user-3',
              firstName: 'Emily',
              lastName: 'Rodriguez',
              email: 'emily.rodriguez@company.com',
              title: 'Backend Developer',
              department: 'Engineering',
              avatar: null,
              role: 'Developer',
              joinedAt: '2023-03-15T00:00:00.000Z',
              status: 'active'
            }
          ]
        },
        {
          id: 'team-2',
          name: 'Product Team',
          description: 'Product strategy and management',
          memberCount: 5,
          leadId: 'user-4',
          leadName: 'David Kim',
          members: [
            {
              id: 'user-4',
              firstName: 'David',
              lastName: 'Kim',
              email: 'david.kim@company.com',
              title: 'Product Manager',
              department: 'Product',
              avatar: null,
              role: 'Team Lead',
              joinedAt: '2023-01-20T00:00:00.000Z',
              status: 'active'
            },
            {
              id: 'user-5',
              firstName: 'Lisa',
              lastName: 'Wang',
              email: 'lisa.wang@company.com',
              title: 'Product Analyst',
              department: 'Product',
              avatar: null,
              role: 'Analyst',
              joinedAt: '2023-04-01T00:00:00.000Z',
              status: 'active'
            }
          ]
        },
        {
          id: 'team-3',
          name: 'Design Team',
          description: 'User experience and visual design',
          memberCount: 4,
          leadId: 'user-6',
          leadName: 'Alex Thompson',
          members: [
            {
              id: 'user-6',
              firstName: 'Alex',
              lastName: 'Thompson',
              email: 'alex.thompson@company.com',
              title: 'Lead UX Designer',
              department: 'Design',
              avatar: null,
              role: 'Team Lead',
              joinedAt: '2023-02-15T00:00:00.000Z',
              status: 'active'
            },
            {
              id: 'user-7',
              firstName: 'Maria',
              lastName: 'Garcia',
              email: 'maria.garcia@company.com',
              title: 'Visual Designer',
              department: 'Design',
              avatar: null,
              role: 'Designer',
              joinedAt: '2023-05-01T00:00:00.000Z',
              status: 'active'
            }
          ]
        }
      ];

      // Apply filters if provided
      let filteredTeamMembers = mockTeamMembers;

      if (params?.teamId) {
        filteredTeamMembers = filteredTeamMembers.filter(team => team.id === params.teamId);
      }

      if (params?.department) {
        filteredTeamMembers = filteredTeamMembers.filter(team =>
          team.members.some(member => member.department === params.department)
        );
      }

      return {
        success: true,
        data: filteredTeamMembers
      };
    },
    enabled: isAuthenticated && !!user?.id,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

// Team-specific hooks for recurring feedback, 1-on-1 setups, and sessions
export const useTeamRecurringFeedback = (params: { teamId?: string }) => {
  const isAuthenticated = useIsAuthenticated();
  const { user } = useAuth();

  return useQuery({
    queryKey: ['teamRecurringFeedback', params.teamId],
    queryFn: async () => {
      if (!params.teamId) return { success: true, data: [] };
      const response = await api.get(`/teams/${params.teamId}/recurring-feedback`);
      return response;
    },
    enabled: isAuthenticated && !!user?.id && !!params.teamId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export const useTeamRecurringFeedbackHistory = (params: {
  teamId?: string;
  cycleId?: string;
  enabled?: boolean;
}) => {
  const isAuthenticated = useIsAuthenticated();
  const { user } = useAuth();

  return useQuery({
    queryKey: ['teamRecurringFeedbackHistory', params.teamId, params.cycleId],
    queryFn: async () => {
      if (!params.teamId || !params.cycleId) {
        return { success: true, data: null };
      }
      const response = await api.get(`/teams/${params.teamId}/recurring-feedback/${params.cycleId}/history`);
      return response;
    },
    enabled:
      (params.enabled ?? true) &&
      isAuthenticated &&
      !!user?.id &&
      !!params.teamId &&
      !!params.cycleId,
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useCreateTeamRecurringFeedback = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      teamId: string;
      name: string;
      description?: string;
      targetUserId: string;
      participantIds: string[];
      schedule: {
        frequency: string;
        startDate: string;
        startTime: string;
        timezone?: string;
        cronSchedule: string;
      };
      templateId?: string;
      questions: Array<{
        id?: string;
        text: string;
        type: string;
        required?: boolean;
        options?: string[];
      }>;
    }) => {
      const { teamId, ...data } = payload;
      return await api.post(`/teams/${teamId}/recurring-feedback`, data);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['teamRecurringFeedback', variables.teamId] });
      queryClient.invalidateQueries({ queryKey: ['team', variables.teamId] });
      return data;
    },
  });
};

export const useUpdateTeamRecurringFeedback = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      teamId: string;
      cycleId: string;
      name: string;
      description?: string;
      targetUserId: string;
      participantIds: string[];
      schedule: {
        frequency: string;
        startDate: string;
        startTime: string;
        timezone?: string;
        cronSchedule: string;
      };
      templateId?: string;
      questions: Array<{
        id?: string;
        text: string;
        type: string;
        required?: boolean;
        options?: string[];
      }>;
    }) => {
      const { teamId, cycleId, ...data } = payload;
      return await api.put(`/teams/${teamId}/recurring-feedback/${cycleId}`, data);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['teamRecurringFeedback', variables.teamId] });
      queryClient.invalidateQueries({ queryKey: ['team', variables.teamId] });
      return data;
    },
  });
};

export const useSendTeamRecurringFeedbackNow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ teamId, cycleId }: { teamId: string; cycleId: string }) => {
      return await api.post(`/teams/${teamId}/recurring-feedback/${cycleId}/send-now`);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['teamRecurringFeedback', variables.teamId] });
      queryClient.invalidateQueries({ queryKey: ['team', variables.teamId] });
      return data;
    },
  });
};

export const useSendTeamRecurringFeedbackParticipant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      teamId,
      cycleId,
      participantId,
    }: {
      teamId: string;
      cycleId: string;
      participantId: string;
    }) => {
      return await api.post(`/teams/${teamId}/recurring-feedback/${cycleId}/send-participant`, {
        participantId,
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['teamRecurringFeedback', variables.teamId] });
      queryClient.invalidateQueries({ queryKey: ['team', variables.teamId] });
      return data;
    },
  });
};

export const useToggleTeamRecurringFeedback = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      teamId,
      cycleId,
      action,
    }: {
      teamId: string;
      cycleId: string;
      action?: 'pause' | 'resume';
    }) => {
      return await api.post(`/teams/${teamId}/recurring-feedback/${cycleId}/pause`, action ? { action } : undefined);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['teamRecurringFeedback', variables.teamId] });
      queryClient.invalidateQueries({ queryKey: ['team', variables.teamId] });
      return data;
    },
  });
};

export const useDeleteTeamRecurringFeedback = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ teamId, cycleId }: { teamId: string; cycleId: string }) => {
      return await api.delete(`/teams/${teamId}/recurring-feedback/${cycleId}`);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['teamRecurringFeedback', variables.teamId] });
      queryClient.invalidateQueries({ queryKey: ['team', variables.teamId] });
      return data;
    },
  });
};

export const useTeamOneOnOneSetups = (params: { teamId?: string }) => {
  const isAuthenticated = useIsAuthenticated();
  const { user } = useAuth();

  return useQuery({
    queryKey: ['teamOneOnOneSetups', params.teamId],
    queryFn: async () => {
      if (!params.teamId) return { success: true, data: [] };
      const response = await api.get(`/teams/${params.teamId}/one-on-one-setups`);
      return response;
    },
    enabled: isAuthenticated && !!user?.id && !!params.teamId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export const useTeamOneOnOneSessions = (params: { teamId?: string }) => {
  const isAuthenticated = useIsAuthenticated();
  const { user } = useAuth();

  return useQuery({
    queryKey: ['teamOneOnOneSessions', params.teamId],
    queryFn: async () => {
      if (!params.teamId) return { success: true, data: [] };
      const response = await api.get(`/teams/${params.teamId}/one-on-one-sessions`);
      return response;
    },
    enabled: isAuthenticated && !!user?.id && !!params.teamId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

// One-on-One Setup Mutation Hooks
export const useCreateOneOnOneSetup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (setupData: {
      teamId: string;
      title: string;
      managerId: string;
      participantId: string;
      workflowType: 'email' | 'physical';
      questions: Array<{
        id: string;
        text: string;
        type: string;
        isRequired: boolean;
        options: string[];
      }>;
      duration: number;
      frequency?: 'weekly' | 'bi-weekly' | 'monthly' | 'manual' | 'custom';
      schedulePattern?: 'weekly' | 'bi-weekly' | 'monthly' | 'manual' | 'custom';
      startDate?: string;
      startTime?: string;
      defaultAgenda?: string[];
      isActive?: boolean;
      emailDueDays?: number;
      saveAsTemplate?: boolean;
      templateName?: string;
      templateDescription?: string;
      action?: string;
    }) => {
      const response = await api.post(`/teams/${setupData.teamId}/one-on-one-setups`, {
        ...setupData,
        employeeId: setupData.participantId,
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['teamOneOnOneSetups', variables.teamId] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });
};

export const useUpdateOneOnOneSetup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (setupData: {
      setupId: string;
      teamId: string;
      title?: string;
      managerId?: string;
      participantId?: string;
      workflowType?: 'email' | 'physical';
      frequency?: 'weekly' | 'bi-weekly' | 'monthly' | 'manual' | 'custom';
      schedulePattern?: 'weekly' | 'bi-weekly' | 'monthly' | 'manual' | 'custom';
      startDate?: string;
      startTime?: string;
      duration?: number;
      defaultAgenda?: string[];
      questions?: Array<{
        id: string;
        text: string;
        type: string;
        isRequired: boolean;
        options: string[];
      }>;
      isActive?: boolean;
      emailDueDays?: number;
    }) => {
      const { setupId, teamId, ...updateData } = setupData;
      const response = await api.put(`/teams/${teamId}/one-on-one-setups/${setupId}`, {
        ...updateData,
        employeeId: updateData.participantId,
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['teamOneOnOneSetups', variables.teamId] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });
};

export const useManualSendOneOnOneSetup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      teamId,
      setupId,
    }: {
      teamId: string;
      setupId: string;
    }) => {
      const response = await api.post(`/teams/${teamId}/one-on-one-setups/${setupId}/manual-send`);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['teamOneOnOneSetups', variables.teamId] });
      queryClient.invalidateQueries({ queryKey: ['teamOneOnOneSessions', variables.teamId] });
    },
  });
};

export const useDeleteOneOnOneSetup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ setupId, teamId }: { setupId: string; teamId: string }) => {
      const response = await api.delete(`/teams/${teamId}/one-on-one-setups/${setupId}`);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['teamOneOnOneSetups', variables.teamId] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });
};

// One-on-One Session Mutation Hooks
export const useCreateOneOnOneSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionData: {
      teamId: string;
      setupId?: string;
      participantId: string;
      managerId: string;
      scheduledDate: string;
      duration: number;
      agenda: string[];
    }) => {
      const response = await api.post(`/teams/${sessionData.teamId}/one-on-one-sessions`, sessionData);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['teamOneOnOneSessions', variables.teamId] });
      queryClient.invalidateQueries({ queryKey: ['oneOnOneSessions'] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });
};

export const useUpdateOneOnOneSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionData: {
      sessionId: string;
      teamId: string;
      scheduledDate?: string;
      duration?: number;
      agenda?: string[];
      status?: 'scheduled' | 'completed' | 'cancelled' | 'rescheduled';
      notes?: string;
      actionItems?: string[];
    }) => {
      const { sessionId, teamId, ...updateData } = sessionData;
      const response = await api.put(`/teams/${teamId}/one-on-one-sessions/${sessionId}`, updateData);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['teamOneOnOneSessions', variables.teamId] });
      queryClient.invalidateQueries({ queryKey: ['oneOnOneSessions'] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });
};

export const useDeleteOneOnOneSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sessionId, teamId }: { sessionId: string; teamId: string }) => {
      const response = await api.delete(`/teams/${teamId}/one-on-one-sessions/${sessionId}`);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['teamOneOnOneSessions', variables.teamId] });
      queryClient.invalidateQueries({ queryKey: ['oneOnOneSessions'] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });
};

// Organization Members Mutation Hooks
export const useInviteOrganizationMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (memberData: {
      organizationId: string;
      email: string;
      role: 'admin' | 'manager' | 'staff';
      firstName?: string;
      lastName?: string;
      departmentId?: string;
      managerId?: string;
      teamIds?: string[];
    }) => {
      const { organizationId, role, ...rest } = memberData;
      const payload = {
        ...rest,
        role: role.toUpperCase(),
      } as Record<string, unknown> & { role: string };
      const response = await api.post(`/organizations/${organizationId}/members/invite`, payload);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['organizationMembers', variables.organizationId] });
    },
  });
};

export const useUpdateOrganizationMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (memberData: {
      organizationId: string;
      memberId: string;
      role?: 'owner' | 'admin' | 'manager' | 'staff';
      firstName?: string;
      lastName?: string;
      departmentId?: string;
      managerId?: string;
      teamIds?: string[];
      isActive?: boolean;
    }) => {
      const { organizationId, memberId, role, ...rest } = memberData;
      const payload = {
        ...rest,
        ...(role ? { role: role.toUpperCase() } : {}),
      };
      const response = await api.put(`/organizations/${organizationId}/members/${memberId}`, payload);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['organizationMembers', variables.organizationId] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });
};

export const useRemoveOrganizationMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (memberData: {
      organizationId: string;
      memberId: string;
      replacementMemberId?: string;
      transferData?: boolean;
    }) => {
      const { organizationId, memberId, ...data } = memberData;
      const response = await api.delete(`/organizations/${organizationId}/members/${memberId}`, { data });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['organizationMembers', variables.organizationId] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });
};

export const useBulkRemoveOrganizationMembers = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (memberData: {
      organizationId: string;
      memberIds: string[];
      replacementMemberId?: string;
      transferData?: boolean;
    }) => {
      const { organizationId, ...data } = memberData;
      const response = await api.post(`/organizations/${organizationId}/members/bulk-remove`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['organizationMembers', variables.organizationId] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });
};

export const useResendOrganizationInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      invitationId,
    }: {
      organizationId: string;
      invitationId: string;
    }) => {
      return await api.post(`/organizations/${organizationId}/invitations/${invitationId}/resend`);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['organizationMembers', variables.organizationId] });
    },
  });
};

export const useCancelOrganizationInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      invitationId,
    }: {
      organizationId: string;
      invitationId: string;
    }) => {
      return await api.delete(`/organizations/${organizationId}/invitations/${invitationId}`);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['organizationMembers', variables.organizationId] });
    },
  });
};

export const useResendVerificationEmail = () => {
  return useMutation({
    mutationFn: async ({ email }: { email: string }) => {
      return await api.post('/auth/resend-verification', { email });
    },
  });
};
